import React, { useContext, useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import dayjs from '../../utils/dayjsConfig'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'

import Layout from '../Layout/Layout'
import InformationModal from '../Modals/ConfirmationModal/InformationModal'

import { addPeriodType } from '../../services/ParametersService'
import { computeBackendDayLimit } from '../../utils/quincena'

import { ParametroContext } from '../../context/parametro/ParametroContext'

const periodsBefore = [
  {
    id: 1,
    value: '1',
  },
  {
    id: 2,
    value: '2',
  },
  {
    id: 3,
    value: '3',
  },
  {
    id: 4,
    value: '4',
  },
  {
    id: 5,
    value: '5',
  },
]

const CrearPeriodo = () => {
  const { t } = useTranslation('parametros')

  const durationArray = [
    { id: 0, value: t('duration.monthly') },
    { id: 1, value: t('duration.biweekly') },
  ]

  const [openInformationModal, setOpenInformationModal] = useState<boolean>(false)
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const [clickCancelar, setClickCancelar] = useState<boolean>(false)
  const [clickGuardar, setClickGuardar] = useState<boolean>(false)
  const [disabledOnSubmit, setDisabledOnSubmit] = useState<boolean>(false)
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false)

  const navigate = useNavigate()

  const { getAccessTokenSilently } = useAuth0()

  const { setTabValue, tabValue } = useContext(ParametroContext)
  // Recalcular mitad actual automáticamente (1-15 primera, 16-fin segunda) y refrescar a medianoche local
  const [now, setNow] = useState(dayjs().tz('America/Argentina/Buenos_Aires'))
  const todayIsFirstHalf = now.date() <= 15

  const [searchParams] = useSearchParams()
  const filterYear = Number(searchParams.get('year'))
  const filterMonth = Number(searchParams.get('month'))
  const defaultCalendarMonth =
    filterYear > 0 && filterMonth >= 1 && filterMonth <= 12
      ? dayjs().year(filterYear).month(filterMonth - 1).startOf('month')
      : undefined

  useEffect(() => {
    // Programar actualización exactamente en la próxima medianoche local para no depender de interacciones
    const current = dayjs().tz('America/Argentina/Buenos_Aires')
    const nextMidnight = current.add(1, 'day').startOf('day')
    const msToNextMidnight = nextMidnight.diff(current)
    const timer = setTimeout(
      () => setNow(dayjs().tz('America/Argentina/Buenos_Aires')),
      msToNextMidnight + 1000,
    )
    return () => clearTimeout(timer)
  }, [now])

  const formik = useFormik({
    initialValues: {
      name: '',
      duration: 1,
      // day_limit ahora es una fecha (string ISO) o ''
      day_limit: '',
      periods_before: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().max(55).required(t('periodos.crear.validation.nameRequired')),
      duration: Yup.number()
        .max(365)
        .required(t('periodos.crear.validation.durationRequired')),
      day_limit: Yup.string()
        .test(
          'is-date',
          t('periodos.crear.validation.invalidDate'),
          value => !!value && dayjs(value).isValid(),
        )
        .test(
          'not-past',
          t('periodos.crear.validation.notPast'),
          value =>
            !!value && !dayjs(value).startOf('day').isBefore(dayjs().startOf('day')),
        )
        .required(t('periodos.crear.validation.dayLimitRequired')),
    }),
    onSubmit: async data => {
      setDisabledOnSubmit(true)
      const token = await getAccessTokenSilently()
      let computedDayLimit: number | null = null
      if (data.day_limit) {
        computedDayLimit = computeBackendDayLimit(Number(data.duration), data.day_limit)
      }
      const payload = {
        ...data,
        day_limit: computedDayLimit,
        reference_date: data.day_limit,
      }
      const result = await addPeriodType(token, payload)

      if (result.status === 201 || result.status === 200) {
        formik.resetForm()
        setTabValue(0)
        navigate(-1)
        handleCloseModal()
      }
    },
  })
  const primeraQuincenaTest = formik.values.day_limit

  // Limpiar fecha si al cambiar a quincenal queda fuera de la quincena actual
  useEffect(() => {
    if (formik.values.duration !== 1) return
    const current = formik.values.day_limit
    if (!current) return
    const d = dayjs(current)
    const isValid = todayIsFirstHalf ? d.date() <= 15 : d.date() > 15
    if (!isValid) {
      formik.setFieldValue('day_limit', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.duration, todayIsFirstHalf])
  const handleClickGuardar = () => {
    setIsDirty(false)
    setClickCancelar(false)
    setClickGuardar(true)
    handleOpenModal()
  }

  const handleOpenModal = () => {
    setOpenInformationModal(true)
  }

  const handleCloseModal = () => {
    setOpenInformationModal(false)
  }

  const handleClickDirtyCancelar = () => {
    setIsDirty(true)
    setClickGuardar(false)
    setClickCancelar(true)
    handleOpenModal()
  }

  const handleDeshacerCancelar = () => {
    formik.resetForm()
    navigate(-1)
  }

  const handleSaveButton = () => {
    formik.submitForm()
  }

  useEffect(() => {
    document.title = `${window._env_.REACT_APP_EMPRESA_NAME} - ${t('periodos.crear.pageTitle')}`

    if (tabValue !== 0) {
      setTabValue(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue])

  return (
    <Layout title={t('periodos.crear.layoutTitle')} noIcon>
      <Grid container marginTop="12px" borderRadius="6px">
        <Grid bgcolor="white" item xs={8} borderRadius="6px">
          <Box
            sx={{
              borderRadius: '6px',
              display: 'flex',
              backgroundColor: 'white',
              height: '100%',
              width: '100%',
              paddingRight: '18px',
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Box
                component="form"
                autoComplete="off"
                borderRadius="6px"
                sx={{ backgroundColor: 'white', height: '100%' }}
                onSubmit={formik.handleSubmit}
              >
                <Grid container justifyContent="space-between">
                  <Grid item xs={6} sx={{ pt: '18px', pl: '18px' }}>
                    <Box display="flex" justifyContent="flex-start" marginBottom="4px">
                      <InputLabel sx={{ color: 'black' }} htmlFor="nombre-input">
                        {t('periodos.crear.fields.name')}
                      </InputLabel>
                    </Box>
                    <TextField
                      id="nombre-input"
                      error={Boolean(formik.touched.name && formik.errors.name)}
                      fullWidth
                      helperText={formik.touched.name && formik.errors.name}
                      name="name"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      type="text"
                      value={formik.values.name}
                      variant="filled"
                      placeholder={t('periodos.crear.fields.namePlaceholder')}
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          borderRadius: '6px',
                          '.MuiInputBase-input': {
                            paddingBottom: '16px',
                            paddingTop: '16px',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} sx={{ pt: '18px', pl: '18px' }}>
                    <Box display="flex" justifyContent="flex-start" marginBottom="4px">
                      <InputLabel sx={{ color: 'black' }} htmlFor="duration-input">
                        {t('periodos.crear.fields.duration')}
                      </InputLabel>
                    </Box>
                    <TextField
                      id="duration-select"
                      select
                      error={Boolean(formik.touched.duration && formik.errors.duration)}
                      fullWidth
                      helperText={formik.touched.duration && formik.errors.duration}
                      name="duration"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      type="number"
                      value={formik.values.duration}
                      variant="filled"
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          borderRadius: '6px',
                          '.MuiInputBase-input': {
                            paddingBottom: '16px',
                            paddingTop: '16px',
                            display: 'flex',
                            justifyContent: 'flex-start',
                          },
                        },
                      }}
                    >
                      {durationArray.map(duration => (
                        <MenuItem key={duration.id} value={duration.id}>
                          {duration.value}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
                <Grid container justifyContent="space-between" paddingBottom="26px">
                  <Grid item xs={6} sx={{ py: '10px', pl: '18px' }}>
                    <Box display="flex" justifyContent="flex-start" marginBottom="4px">
                      <InputLabel sx={{ color: 'black' }} htmlFor="day-limit-input">
                        {t('periodos.crear.fields.dayLimit')}
                      </InputLabel>
                    </Box>
                    <DesktopDatePicker
                      inputFormat="DD/MM/YYYY"
                      open={openDatePicker}
                      onOpen={() => setOpenDatePicker(true)}
                      onClose={() => setOpenDatePicker(false)}
                      defaultCalendarMonth={defaultCalendarMonth}
                      value={
                        formik.values.day_limit ? dayjs(formik.values.day_limit) : null
                      }
                      minDate={now.startOf('day')}
                      disablePast
                      shouldDisableDate={date => {
                        if (formik.values.duration !== 1) return false // mensual no restringe
                        // Si estamos en primera quincena, bloquear días > 15
                        if (todayIsFirstHalf) return date.date() > 15
                        // Si estamos en segunda quincena, bloquear días <= 15
                        return date.date() <= 15
                      }}
                      onChange={newValue => {
                        if (newValue && dayjs(newValue).isValid()) {
                          formik.setFieldValue(
                            'day_limit',
                            dayjs(newValue).startOf('day').toISOString(),
                          )
                        } else {
                          formik.setFieldValue('day_limit', '')
                        }
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          id="day-limit-input"
                          name="day_limit"
                          variant="filled"
                          fullWidth
                          error={Boolean(
                            formik.touched.day_limit && formik.errors.day_limit,
                          )}
                          helperText={formik.touched.day_limit && formik.errors.day_limit}
                          onBlur={formik.handleBlur}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                            onClick: () => setOpenDatePicker(true),
                            sx: {
                              borderRadius: '6px',
                              cursor: 'pointer',
                              '.MuiInputBase-input': {
                                paddingBottom: '16px',
                                paddingTop: '16px',
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sx={{ py: '10px', pl: '18px' }}>
                    <Box display="flex" justifyContent="flex-start" marginBottom="4px">
                      <InputLabel sx={{ color: 'black' }} htmlFor="periods-before-select">
                        {t('periodos.crear.fields.advance')}
                      </InputLabel>
                    </Box>
                    <TextField
                      id="periods-before-select"
                      select
                      error={Boolean(
                        formik.touched.periods_before && formik.errors.periods_before,
                      )}
                      fullWidth
                      helperText={
                        formik.touched.periods_before && formik.errors.periods_before
                      }
                      name="periods_before"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.periods_before}
                      variant="filled"
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          borderRadius: '6px',
                          '.MuiInputBase-input': {
                            paddingBottom: '16px',
                            paddingTop: '16px',
                            display: 'flex',
                            justifyContent: 'flex-start',
                          },
                        },
                      }}
                    >
                      {periodsBefore.map(period => (
                        <MenuItem key={period.id} value={period.value}>
                          {period.value}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
                <Grid container maxWidth="sm" paddingBottom="32px">
                  <Box sx={{ display: 'flex' }}>
                    <Button
                      sx={{ marginLeft: '18px' }}
                      variant="contained"
                      color="primary"
                      onClick={handleClickGuardar}
                      disabled={
                        !formik.dirty ||
                        !formik.values.name ||
                        !formik.values.day_limit ||
                        !formik.values.periods_before ||
                        !(formik.values.duration >= 0 && formik.values.duration <= 1)
                      }
                    >
                      {t('actions.save', { ns: 'common' })}
                    </Button>

                    {formik.dirty ? (
                      <Button
                        sx={{ marginLeft: '18px' }}
                        variant="contained"
                        color="error"
                        onClick={handleClickDirtyCancelar}
                      >
                        {t('actions.cancel', { ns: 'common' })}
                      </Button>
                    ) : (
                      <Button
                        sx={{ marginLeft: '18px' }}
                        variant="contained"
                        color="error"
                        onClick={() => navigate(-1)}
                      >
                        {t('actions.cancel', { ns: 'common' })}
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {openInformationModal && (
        <InformationModal
          title={isDirty ? t('periodos.crear.modals.cancelTitle') : t('periodos.crear.modals.createTitle')}
          message={
            isDirty
              ? t('periodos.crear.modals.cancelChangesMessage')
              : t('periodos.crear.modals.createMessage')
          }
          openModal={openInformationModal}
          onClose={handleCloseModal}
          saveForm={handleSaveButton}
          isDirty={isDirty}
          clickCancelar={clickCancelar}
          handleDeshacerCancelar={handleDeshacerCancelar}
          guardar={clickGuardar}
          guardarMessage={t('periodos.crear.modals.createMessage')}
          clickEliminar={false}
          disabledOnSubmit={disabledOnSubmit}
        />
      )}
    </Layout>
  )
}

export default CrearPeriodo
